/**
 * Hooks for the per-campaign AI preview + reply simulator.
 *
 * Both endpoints are dry-runs — they generate AI messages without sending
 * anything to LinkedIn or persisting to D1. Used for the demo dialog so
 * the operator can show "this is what the AI would write to each lead,
 * and this is how it would respond to a reply."
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/use-api'

// --- Preview messages -----------------------------------------------------

export type PreviewStep = {
  readonly type: string
  readonly strategy: string
  readonly message: string | null
  readonly error?: string
  readonly tokens_used?: number
  /** True for steps sent without a body — currently only `connection_request`. */
  readonly no_body?: boolean
}

/**
 * Per-input-source breakdown of what reached the AI for this lead. The
 * preview dialog renders this in a collapsible "AI Context" panel so the
 * operator can verify the lead's About / posts / FAQs / etc. actually
 * landed in the prompt — and which ones were missing.
 *
 * Mirrors the backend shape from
 * workers/campaigns/src/routes/preview.ts → buildLeadPreview.
 */
export type PreviewAIContextSection = {
  readonly key: string
  readonly label: string
  readonly status: 'loaded' | 'missing' | 'empty'
  readonly detail: string
  readonly excerpt?: string
}

export type PreviewAIContext = {
  readonly sections: readonly PreviewAIContextSection[]
  /** Full assembled system prompt for the first generative step (skill.md elided). */
  readonly system_prompt_first_step?: string
  /** User prompt for the first generative step. */
  readonly user_prompt_first_step?: string
}

export type PreviewLead = {
  readonly lead_id: string
  readonly name: string
  readonly title: string | null
  readonly company: string | null
  readonly linkedin_url: string | null
  readonly steps: readonly PreviewStep[]
  /**
   * Optional because KV-cached previews generated before this feature
   * shipped don't include it. The dialog renders a "regenerate to see"
   * affordance in that case.
   */
  readonly ai_context?: PreviewAIContext
}

export type PreviewResponse = {
  readonly leads: readonly PreviewLead[]
  readonly generated_at: string
  readonly cached: boolean
  readonly campaign_id: string
  readonly campaign_name: string
  readonly lead_count_total: number
  readonly lead_count_previewed: number
  readonly step_count: number
}

type PreviewMutationInput = {
  readonly campaignId: string
  readonly leadLimit?: number
  readonly force?: boolean
}

/**
 * Mutation form: explicit "generate" trigger. Caches in React Query
 * under the campaign id so the dialog can show the most recent run
 * without re-firing on mount.
 */
export function usePreviewMessages() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ campaignId, leadLimit, force }: PreviewMutationInput): Promise<PreviewResponse> => {
      const res = await apiFetch<PreviewResponse>(
        `/api/campaigns/${campaignId}/preview-messages`,
        { method: 'POST', body: { lead_limit: leadLimit, force } },
      )
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Failed to generate preview')
      }
      return res.data
    },
    onSuccess: (data, { campaignId }) => {
      // Cache the result under a stable key so the dialog can re-read
      // it without re-running the mutation on remount.
      queryClient.setQueryData(['campaign-preview', campaignId], data)
    },
  })
}

/** Read-only accessor for the most recent preview result for a campaign. */
export function useCachedPreview(campaignId: string | null): PreviewResponse | undefined {
  const queryClient = useQueryClient()
  if (!campaignId) return undefined
  return queryClient.getQueryData<PreviewResponse>(['campaign-preview', campaignId])
}

// --- Reply simulator ------------------------------------------------------

export type SimulatorMessage = {
  /** Who "sent" the message in the simulated thread. */
  readonly from: 'them' | 'you'
  readonly content: string
}

type Classification = {
  readonly category?: string
  readonly confidence?: number
  readonly reasoning?: string
  readonly detected_intent?: string
}

export type SimulateReplyResponse = {
  readonly lead_id: string
  readonly lead_name: string
  readonly classification: Classification | null
  readonly classification_error?: string
  readonly ai_reply: string | null
  readonly ai_reply_error?: string
  readonly generated_at: string
}

type SimulateReplyInput = {
  readonly campaignId: string
  readonly leadId: string
  readonly conversation: readonly SimulatorMessage[]
  readonly simulatedReply: string
}

export function useSimulateReply() {
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async ({ campaignId, leadId, conversation, simulatedReply }: SimulateReplyInput): Promise<SimulateReplyResponse> => {
      const res = await apiFetch<SimulateReplyResponse>(
        `/api/campaigns/${campaignId}/simulate-reply`,
        {
          method: 'POST',
          body: {
            lead_id: leadId,
            conversation,
            simulated_reply: simulatedReply,
          },
        },
      )
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Failed to simulate reply')
      }
      return res.data
    },
  })
}

// Dummy export to silence the no-unused-import warning — useQuery is for
// callers that need a non-mutation accessor in the future.
export { useQuery as _useQuery }
